import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { Auth, ErrorResponse, NewTokenResponse } from '~/services/auth-api';

// TODO: create a constructor to define a singleton that will receive
// the location and name of the access / refresh tokens

export const instance = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_ORCA_API_RESOURCE}/v1`,
});

instance.interceptors.request.use((req: InternalAxiosRequestConfig) => {
  const accessToken = window.localStorage.accessToken;
  const refreshToken = window.localStorage.refreshToken;
  const excludeAuthorization =
    !!req.params?.excludeAuthorization || !!req.data?.excludeAuthorization;
  if (accessToken && refreshToken && !excludeAuthorization) {
    req.headers['X-AUTH-TOKEN'] = `${accessToken}`;
  } else {
    delete req.headers['X-AUTH-TOKEN'];
    delete req.params?.excludeAuthorizationHeader;
  }

  return req;
});

instance.interceptors.response.use(
  async (response: AxiosResponse) => response,
  async error => {
    const originalRequest = error.config;
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = window.localStorage.getItem(
          'refreshToken'
        ) as string;
        const newAccessToken: NewTokenResponse | ErrorResponse =
          await Auth.refreshAccessToken({ refreshToken });

        if ((newAccessToken as ErrorResponse).errorCode) {
          location.href = '/login';
          return Promise.reject(error);
        }

        window.localStorage.setItem(
          'accessToken',
          (newAccessToken as NewTokenResponse).accessToken
        );

        instance.defaults.headers.common['X-AUTH-TOKEN'] = (
          newAccessToken as NewTokenResponse
        ).accessToken;
        return instance(originalRequest);
      } catch (error) {
        window.localStorage.removeItem('accessToken');
        window.localStorage.removeItem('refreshToken');
        location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

type httpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

async function request<R = any, T = any>(
  method: httpMethod,
  url: string,
  data?: T
) {
  const response: AxiosResponse = await instance[method](
    url,
    method === 'get'
      ? ({
          params: data,
        } as AxiosRequestConfig)
      : data
  );
  return response.data as R;
}

export const requests = {
  async get<R = any, T = any>(url: string, data?: T) {
    return request<R, T>('get', url, data);
  },
  async post<R = any, T = any>(url: string, data?: T) {
    return request<R, T>('post', url, data);
  },
  async put<R = any, T = any>(url: string, data?: T) {
    return request<R, T>('put', url, data);
  },
  async patch<R = any, T = any>(url: string, data?: T) {
    return request<R, T>('patch', url, data);
  },
  async delete<R = any, T = any>(url: string, data?: T) {
    return request<R, T>('delete', url, data);
  },
};
